/**
 * Vendor — a service-provider entry in an owner's HomeHQ directory.
 *
 * Two sources:
 *   - 'google_places': discovered via Google Places (Hauser-EQ proxy or
 *     direct), `place_id` is the canonical reference.
 *   - 'manual': owner typed it in (small contractor with no listing, etc.).
 *
 * `pinned: true` means the vendor is saved in the owner's directory.
 * Auto-populate flows pin top-N by quality score per trade.
 *
 * Shape is FE-led — drives the BE contract.
 */

/**
 * Vendor trades — canonical list, kept in lockstep with TRADE_OPTIONS in
 * `packages/functions/common/models/Issue.ts`, which the comment there
 * notes "mirrors the hardcoded Directory contact categories in
 * ContactDBClient.getHardcodedParentContacts". Order here matches that
 * source. Duplicated (not imported) because FE can't reach across to
 * the functions package — keep them aligned manually.
 */
export type VendorTrade =
  | 'general_contracting'
  | 'plumbing'
  | 'electrical'
  | 'air_conditioning_and_heat'
  | 'roofing'
  | 'landscaping'
  | 'solar'
  | 'appliances'
  | 'structural'
  | 'pest_control'
  | 'mold_and_water_damage'
  | 'handyman'
  | 'security'
  | 'smart_home'
  | 'painting'
  | 'home_inspection'
  | 'septic_and_sewer'
  | 'pool_and_spa'
  | 'windows_and_doors'
  | 'flooring'
  | 'cleaning_services'
  | 'fencing'
  | 'snow_removal'
  | 'real_estate'
  | 'insurance'
  | 'public_adjuster'
  | 'title_escrow_and_legal'
  | 'mortgage'
  | 'other_specialty';

export interface VendorDistance {
  value: number;
  unit: 'mi' | 'km';
}

export interface VendorLocation {
  lat: number;
  lng: number;
}

export interface Vendor {
  id: string;
  propertyId: string;
  trade: VendorTrade;
  source: 'google_places' | 'manual';
  pinned: boolean;

  name: string;
  contactName?: string;
  place_id?: string;

  phone?: string;
  internationalPhoneNumber?: string;
  email?: string;
  website?: string;
  googleMapsUri?: string;
  address?: string;

  rating?: number;
  reviewCount?: number;
  priceLevel?: number;

  distance?: VendorDistance;
  location?: VendorLocation;

  // Listing detail — sourced from Google Places Details + Photos in prod.
  photoUrl?: string;
  category?: string;
  hoursLabel?: string;

  notes?: string;
  lastContactedAt?: number;
  lastContactedMethod?: 'sms' | 'maps' | 'email' | 'phone' | 'copy';
  lastContactedRfqId?: string;
  lastContactedProjectTitle?: string;

  createdAt: number;
  updatedAt: number;
}

export interface CreateVendorInput {
  propertyId: string;
  trade: VendorTrade;
  source: 'google_places' | 'manual';
  name: string;
  contactName?: string;
  place_id?: string;
  phone?: string;
  internationalPhoneNumber?: string;
  email?: string;
  website?: string;
  googleMapsUri?: string;
  address?: string;
  rating?: number;
  reviewCount?: number;
  priceLevel?: number;
  distance?: VendorDistance;
  location?: VendorLocation;
  photoUrl?: string;
  category?: string;
  hoursLabel?: string;
  notes?: string;
  lastContactedAt?: number;
  lastContactedMethod?: 'sms' | 'maps' | 'email' | 'phone' | 'copy';
  lastContactedRfqId?: string;
  lastContactedProjectTitle?: string;
}

export interface UpdateVendorInput {
  pinned?: boolean;
  contactName?: string;
  phone?: string;
  internationalPhoneNumber?: string;
  email?: string;
  website?: string;
  googleMapsUri?: string;
  notes?: string;
  lastContactedAt?: number;
  lastContactedMethod?: 'sms' | 'maps' | 'email' | 'phone' | 'copy';
  lastContactedRfqId?: string;
  lastContactedProjectTitle?: string;
}

export interface VendorCandidate {
  trade: VendorTrade;
  source: 'google_places';
  name: string;
  place_id: string;
  phone?: string;
  internationalPhoneNumber?: string;
  email?: string;
  website?: string;
  googleMapsUri?: string;
  address?: string;
  rating?: number;
  reviewCount?: number;
  priceLevel?: number;
  distance?: VendorDistance;
  location?: VendorLocation;
  photoUrl?: string;
  category?: string;
  hoursLabel?: string;
}

export interface SearchVendorCandidatesInput {
  propertyId: string;
  trade: VendorTrade;
  /**
   * Free-form Places Text Search query. Required when
   * `trade === 'other_specialty'`; ignored for configured trades.
   * TODO(FE): wire a TextField in VendorFinderDialog when the selected
   * trade is `other_specialty` so the user can type a custom search.
   */
  textQuery?: string;
  filters?: {
    minRating?: number;
    maxDistanceMi?: number;
    maxPriceLevel?: number;
    minReviewCount?: number;
  };
}

export const VENDOR_TRADES: VendorTrade[] = [
  'general_contracting',
  'plumbing',
  'electrical',
  'air_conditioning_and_heat',
  'roofing',
  'landscaping',
  'solar',
  'appliances',
  'structural',
  'pest_control',
  'mold_and_water_damage',
  'handyman',
  'security',
  'smart_home',
  'painting',
  'home_inspection',
  'septic_and_sewer',
  'pool_and_spa',
  'windows_and_doors',
  'flooring',
  'cleaning_services',
  'fencing',
  'snow_removal',
  'real_estate',
  'insurance',
  'public_adjuster',
  'title_escrow_and_legal',
  'mortgage',
  'other_specialty',
];

/**
 * Visual grouping for the directory. Service providers are the
 * break/fix and home-services trades; "Real Estate & Closing" groups
 * the transactional categories (less frequent contact, different
 * relationship shape — but still useful to keep one phone-call away).
 */
export const VENDOR_TRADE_GROUPS: { label: string; trades: VendorTrade[] }[] = [
  {
    label: 'Service Providers',
    trades: [
      'general_contracting',
      'plumbing',
      'electrical',
      'air_conditioning_and_heat',
      'roofing',
      'landscaping',
      'solar',
      'appliances',
      'structural',
      'pest_control',
      'mold_and_water_damage',
      'handyman',
      'security',
      'smart_home',
      'painting',
      'home_inspection',
      'septic_and_sewer',
      'pool_and_spa',
      'windows_and_doors',
      'flooring',
      'cleaning_services',
      'fencing',
      'snow_removal',
      'other_specialty',
    ],
  },
  {
    label: 'Real Estate & Closing',
    trades: ['real_estate', 'insurance', 'public_adjuster', 'title_escrow_and_legal', 'mortgage'],
  },
];

export const VENDOR_TRADE_LABELS: Record<VendorTrade, string> = {
  general_contracting: 'General Contracting',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  air_conditioning_and_heat: 'Air Conditioning & Heat',
  roofing: 'Roofing',
  landscaping: 'Landscaping',
  solar: 'Solar',
  appliances: 'Appliances',
  structural: 'Structural',
  pest_control: 'Pest Control',
  mold_and_water_damage: 'Mold & Water Damage',
  handyman: 'Handyman',
  security: 'Security',
  smart_home: 'Smart Home',
  painting: 'Painting',
  home_inspection: 'Home Inspection',
  septic_and_sewer: 'Septic & Sewer',
  pool_and_spa: 'Pool & Spa',
  windows_and_doors: 'Windows & Doors',
  flooring: 'Flooring',
  cleaning_services: 'Cleaning Services',
  fencing: 'Fencing',
  snow_removal: 'Snow Removal',
  real_estate: 'Real Estate',
  insurance: 'Insurance',
  public_adjuster: 'Public Adjuster',
  title_escrow_and_legal: 'Title, Escrow & Legal',
  mortgage: 'Mortgage',
  other_specialty: 'Other / Specialty',
};
